

import { makeStyles } from 'tss-react/mui';
import { StatusCodes } from 'http-status-codes';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  MUIDataTable,
  MUIDataTableColumnDef,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { lastUpdatedInfoModel } from '../../../../api-refactored/exam-configuration/useGetLastUpdatedInfo';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import EditButton from '../../../../components/buttons/EditButton';
import DraftDateTimeStamp from '../../../../components/draft-date-time-stamp/DraftDateTimeStamp';
import { ValidCeqTestDateAccessControl } from './index';
import {
  useGetValidCeqTestDates,
  ValidCeqTestDateModelResponse,
} from '../../../../api-refactored/exam-configuration/useGetValidCeqTestDates';
import ValidCeqTestDateModal from './ValidCeqTestDateModal';
import ValidCeqTestDateAdmissionYearAndAddButton from './ValidCeqTestDateAdmissionYearAndAddButton';
import ValidCeqTestDateRemoveModal from './ValidCeqTestDateRemoveModal';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
({
  gstInclusive: {
    color: theme.color.text.textTertiary,
  },
  removeButton: {
    [theme.breakpoints.between('md', 'xl')]: {
      paddingLeft: `${theme.spacing(2)} !important`,
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: `${theme.spacing(4)} !important`,
    },
  },
}));

export interface SetStateProps {
  setAddModalOpened: Dispatch<React.SetStateAction<boolean>>;
  setDeleteModalOpened: Dispatch<React.SetStateAction<boolean>>;
  setEditState: Dispatch<React.SetStateAction<boolean>>;
  setSelectedValidCeqTestDateRange: Dispatch<
    React.SetStateAction<ValidCeqTestDateModelResponse | undefined>
  >;
}

const getTableColumn = (
  t: TFunction<'translation'>,
  tableState: TableState,
  classes: Record<string,string>,
  accessControl: ValidCeqTestDateAccessControl,
  setState: SetStateProps
): MUIDataTableColumnDef[] => {
  const {
    setAddModalOpened,
    setDeleteModalOpened,
    setEditState,
    setSelectedValidCeqTestDateRange,
  } = setState;

  const onEditHandler = (dataIndex: number) => {
    setSelectedValidCeqTestDateRange(tableState.data[dataIndex]);
    setEditState(true);
    setAddModalOpened(true);
  };

  const onDeleteHandler = (dataIndex: number) => {
    setSelectedValidCeqTestDateRange(tableState.data[dataIndex]);
    setDeleteModalOpened(true);
  };

  return [
    {
      name: 'firstAcceptedCeqTestDate',
      label: t('exam->>ValidCeqTestDateConfiguration.FirstAcceptedCeqTestDate'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {stringToSemanticDisplay(tableState.data[dataIndex].firstAcceptedCeqTestDate)}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'lastAcceptedCeqTestDate',
      label: t('exam->>ValidCeqTestDateConfiguration.LastAcceptedCeqTestDate'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {stringToSemanticDisplay(tableState.data[dataIndex].lastAcceptedCeqTestDate)}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'actions',
      label: t('exam->>ExamConfiguration.Actions'),
      options: {
        display: accessControl.hasEditAccess || accessControl.hasDeleteAccess,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {accessControl.hasEditAccess && (
              <EditButton
                buttonId={dataIndex}
                editHandler={() => onEditHandler(dataIndex)}
                label={t('components->>IconLabel.Edit')}
              />
            )}
            {accessControl.hasDeleteAccess && (
              <DeleteButton
                buttonId={dataIndex}
                deleteHandler={() => onDeleteHandler(dataIndex)}
                label={t('components->>IconLabel.Remove')}
                className={classes.removeButton}
              />
            )}
          </>
        ),
      },
    },
  ];
};

interface ValidCeqTestDateProps {
  isStrictlyAeisExam: boolean;
  selectedExamTypeUuid: string;
  setNoAccess: Dispatch<SetStateAction<boolean>>;
  accessControl: ValidCeqTestDateAccessControl;
}

const { FORBIDDEN } = StatusCodes;

const ValidCeqTestDate = ({
  isStrictlyAeisExam,
  selectedExamTypeUuid,
  setNoAccess,
  accessControl,
}: ValidCeqTestDateProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [lastUpdatedInfo, setLastUpdatedInfo] = useState<lastUpdatedInfoModel>({
    lastUpdatedTime: null,
    name: null,
  });
  const { state: tableState, dispatch: tableDispatch } = useDataTable();
  const [isAddModalOpened, setAddModalOpened] = useState<boolean>(false);
  const [isDeleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [isEditState, setEditState] = useState<boolean>(false);
  const [
    selectedValidCeqTestDateRange,
    setSelectedValidCeqTestDateRange,
  ] = useState<ValidCeqTestDateModelResponse>();

  const {
    data: validCeqTestDateData,
    isLoading: isLoadingValidCeqTestDateListing,
    refetch: refetchValidCeqTestDateListing,
  } = useGetValidCeqTestDates({
    registrationExamLevelUuid: selectedExamTypeUuid,
    admissionYear: selectedYear ?? Number.NaN,
    enabled: !!selectedExamTypeUuid && !!selectedYear,
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.data,
            count: data.data.length,
          },
        } as TableActionTypes);
        setLastUpdatedInfo({
          lastUpdatedTime: data.lastUpdatedAt,
          name: data.lastUpdatedBy,
        });
      }
    },
    onError: (err) => {
      if (err.response?.status === FORBIDDEN) {
        setNoAccess(true);
      }
    },
  });

  const fetchValidCeqTestDateListing = () => {
    if (selectedYear) {
      refetchValidCeqTestDateListing();
    }
  };

  useEffect(() => {
    fetchValidCeqTestDateListing();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedYear]);

  const onAddHandler = () => {
    setSelectedValidCeqTestDateRange(undefined);
    setEditState(false);
    setAddModalOpened(true);
  };

  return (
    <>
      <ValidCeqTestDateAdmissionYearAndAddButton
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        listingData={validCeqTestDateData}
        addButtonHandler={onAddHandler}
        setNoAccess={setNoAccess}
        hasCreateAuthority={accessControl.hasCreateAccess}
      />

      <MUIDataTable
        key={`${isStrictlyAeisExam ? 'aeis' : 'saeis'}-valid-ceq-test-dates-listing`}
        isLoading={isLoadingValidCeqTestDateListing}
        columns={getTableColumn(t, tableState, classes, accessControl, {
          setAddModalOpened,
          setDeleteModalOpened,
          setEditState,
          setSelectedValidCeqTestDateRange,
        })}
        data={tableState.data}
        dispatch={tableDispatch}
        state={tableState}
        emptyDataLabel={t('exam->>ValidCeqTestDateConfiguration.NoValidCeqTestDateAdded')}
      />

      <DraftDateTimeStamp lastUpdatedInfo={lastUpdatedInfo} />

      {selectedYear && (
        <ValidCeqTestDateModal
          isStrictlyAeisExam={isStrictlyAeisExam}
          isEditState={isEditState}
          isModalOpened={isAddModalOpened}
          setModalOpened={setAddModalOpened}
          defaultData={selectedValidCeqTestDateRange}
          registrationExamLevelUuid={selectedExamTypeUuid}
          year={selectedYear}
          fetchValidCeqTestDateListing={fetchValidCeqTestDateListing}
        />
      )}
      {selectedYear && (
        <ValidCeqTestDateRemoveModal
          year={selectedYear}
          isModalOpened={isDeleteModalOpened}
          setModalOpened={setDeleteModalOpened}
          fetchValidCeqTestDateListing={fetchValidCeqTestDateListing}
          defaultData={selectedValidCeqTestDateRange}
          registrationExamLevelUuid={selectedExamTypeUuid}
          isStrictlyAeisExam={isStrictlyAeisExam}
        />
      )}
    </>
  );
};

export default ValidCeqTestDate;
